// ── Cockpit data shape — shared between trainer-software and healthscore-api ──
export function scoreToBand(score) {
    if (score <= 50)
        return 'red';
    if (score <= 75)
        return 'yellow';
    return 'green';
}
export const BRAND = {
    blue: '#00537e',
    blueDeep: '#004360',
    tint: '#EEF4F7',
    red: '#f93f43',
    yellow: '#efe300',
    green: '#138f38',
};
export const BAND_COLORS = {
    red: BRAND.red,
    yellow: BRAND.yellow,
    green: BRAND.green,
};
export const BAND_LABELS_BY_LOCALE = {
    de: { red: 'Rot', yellow: 'Gelb', green: 'Grün' },
    en: { red: 'Red', yellow: 'Yellow', green: 'Green' },
};
export function getBandLabel(band, locale = 'de') {
    return BAND_LABELS_BY_LOCALE[locale][band];
}
// Back-compat: the German band labels. Prefer getBandLabel(band, locale).
export const BAND_LABELS = BAND_LABELS_BY_LOCALE.de;
// Every user-facing string of the cockpit, keyed per locale — the package has
// no host i18n system, the consuming apps pass `locale` instead.
const UI_STRINGS_BY_LOCALE = {
    de: {
        'lever.currentStateTitle': 'Hebel Ist-Zustand',
        'lever.behaviorTitle': 'Hebel Verhalten',
        'lever.none': 'Kein Hebel ermittelt',
        'lever.noCurrentState': 'Kein Ist-Zustand-Hebel',
        'lever.noBehavior': 'Kein Verhalten-Hebel',
        'lever.badge': 'Hebel',
        'measurementTargets.title': 'Messziele',
        'measurementTargets.reached': 'Ziel erreicht',
        'section.currentState': 'Ist-Zustand',
        'section.behavior': 'Verhalten',
        'score.overall': 'Gesamtscore',
        'score.questionnaireOnly': 'Fragebogen-Score',
        'detail.questionnaire': 'Fragebogen',
        'detail.trainerMeasurement': 'Trainermessung',
        'detail.partialMeasurement': 'Teilmessung',
        'detail.valuesAndTargets': 'Messwerte & Ziele',
        'detail.history': 'Verlauf',
        'detail.forScore': 'für Score',
        'detail.measured': 'Gemessen',
    },
    en: {
        'lever.currentStateTitle': 'Lever current state',
        'lever.behaviorTitle': 'Lever behavior',
        'lever.none': 'No lever determined',
        'lever.noCurrentState': 'No current-state lever',
        'lever.noBehavior': 'No behavior lever',
        'lever.badge': 'Lever',
        'measurementTargets.title': 'Measurement targets',
        'measurementTargets.reached': 'Target reached',
        'section.currentState': 'Current state',
        'section.behavior': 'Behavior',
        'score.overall': 'Overall score',
        'score.questionnaireOnly': 'Questionnaire score',
        'detail.questionnaire': 'Questionnaire',
        'detail.trainerMeasurement': 'Trainer measurement',
        'detail.partialMeasurement': 'Partial measurement',
        'detail.valuesAndTargets': 'Measurements & targets',
        'detail.history': 'History',
        'detail.forScore': 'for score',
        'detail.measured': 'Measured',
    },
};
export function uiText(key, locale = 'de') {
    return UI_STRINGS_BY_LOCALE[locale][key] ?? UI_STRINGS_BY_LOCALE.de[key] ?? key;
}
// Category display labels. Keys = HealthCategory values (source of truth: trainer
// software). Mirrored server-side in healthscore-api translations/messages.*.yaml.
export const CATEGORY_LABELS_BY_LOCALE = {
    de: {
        __overall__: 'Gesamtscore',
        kraft: 'Kraft',
        beweglichkeit: 'Beweglichkeit',
        gleichgewicht: 'Gleichgewicht',
        koerperzusammensetzung: 'Körperzusammensetzung',
        schmerzfreiheit: 'Schmerzfreiheit',
        stressfreiheit: 'Stressfreiheit',
        energie: 'Energie',
        ernaehrung: 'Ernährung',
        mental: 'Mental',
        environment: 'Umfeld',
        regeneration: 'Regeneration',
        training: 'Training',
    },
    en: {
        __overall__: 'Overall score',
        kraft: 'Strength',
        beweglichkeit: 'Mobility',
        gleichgewicht: 'Balance',
        koerperzusammensetzung: 'Body composition',
        schmerzfreiheit: 'Pain-free',
        stressfreiheit: 'Stress-free',
        energie: 'Energy',
        ernaehrung: 'Nutrition',
        mental: 'Mental',
        environment: 'Environment',
        regeneration: 'Recovery',
        training: 'Training',
    },
};
export function getCategoryLabel(key, locale = 'de') {
    return CATEGORY_LABELS_BY_LOCALE[locale][key] ?? key;
}
// Back-compat: the German label map. Prefer getCategoryLabel(key, locale).
export const CATEGORY_LABELS = CATEGORY_LABELS_BY_LOCALE.de;
export const IST_ZUSTAND_CATEGORIES = [
    'kraft',
    'beweglichkeit',
    'gleichgewicht',
    'koerperzusammensetzung',
    'schmerzfreiheit',
    'stressfreiheit',
    'energie',
];
export const VERHALTEN_CATEGORIES = ['ernaehrung', 'mental', 'environment', 'regeneration', 'training'];
